import React from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Provider } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { VerifyEmailAlert } from '~shared/components/VerifyEmailAlert';
import { DesktopNavbar } from '~teacher/components/Header/DesktopNavbar';
import { Logo } from '~teacher/components/Header/Logo';
import { MobileNavbar } from '~teacher/components/Header/MobileNavbar';
import { RoomsPaywall } from '~teacher/components/rooms/RoomsPaywall';
import { RoomsPaywallProvider } from '~teacher/components/rooms/RoomsPaywall/RoomsPaywallProvider';
import { useShouldHideHeader } from '~teacher/context/CurrentRouteContext/hooks';
import { queryClient } from '~teacher/queryClient';

import store from '../../../store';
import { LogoutConfirmationDialog } from './LogoutConfirmationDialog/LogoutConfirmationDialog';
import { PlansOrGetProButton } from './PlansOrGetProButton/PlansOrGetProButton';
import { ProfileMenu } from './ProfileMenu/ProfileMenu';
import { RoomsMenu } from './RoomsMenu/RoomsMenu';
import { StudentMenu } from './StudentMenu/StudentMenu';

export interface Props {
  controller: HeaderController;
  model: HeaderModel;
  roomName: string;
  studentList: Student[];
  enableHandraise: boolean;
  handsRaised: number;
}

// HACK These are simplistic types for typescript. Please update as needed!
type HeaderController = any;
type HeaderModel = { studentsInRoom: number };

interface Student {
  student_id: string;
  handraise: boolean;
  logged_in: boolean;
  id: string;
}

export const Header = ({
  studentList,
  handsRaised,
  controller,
  enableHandraise,
  model,
}: Props) => {
  const isDesktop = useMediaQuery({ query: '(min-width: 959px)' });
  const setIsLogoutConfirmationOpen = useSetAtom(
    LogoutConfirmationDialog.isOpenAtom
  );

  const hideHeader = useShouldHideHeader();

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store} key={'header-nav'}>
        <RoomsPaywallProvider>
          <div
            data-testid="header-container"
            className={`mx-auto px-2 sm:px-6 lg:px-8 ${
              hideHeader ? 'hidden' : ''
            }`}
          >
            <div className="relative flex h-16 items-center justify-between">
              <div className="flex flex-1 items-center justify-center tb:justify-start">
                <div className="flex flex-shrink-0 items-center pr-4">
                  {isDesktop && <Logo className="h-8 w-auto" />}
                </div>
                <nav role="navigation">
                  <DesktopNavbar isVisible={isDesktop} />
                  <MobileNavbar
                    isVisible={!isDesktop}
                    handleSignOut={() => setIsLogoutConfirmationOpen(true)}
                  />
                </nav>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 tb:static tb:inset-auto tb:ml-6 tb:pr-0 gap-1 xl:gap-2">
                <StudentMenu
                  studentList={studentList}
                  handsRaised={handsRaised}
                  controller={controller}
                  enableHandraise={enableHandraise}
                  model={model}
                />
                {!hideHeader && <RoomsMenu />}
                {!hideHeader && <PlansOrGetProButton isVisible={isDesktop} />}
                <ProfileMenu
                  isVisible={isDesktop}
                  handleSignOut={() => setIsLogoutConfirmationOpen(true)}
                />
              </div>
              <LogoutConfirmationDialog />
            </div>
          </div>
          {hideHeader ? null : <VerifyEmailAlert />}
          <RoomsPaywall />
        </RoomsPaywallProvider>
      </Provider>
    </QueryClientProvider>
  );
};
